/**
 * Match setup: the format, where and when, two teams, an XI in batting order, the toss.
 *
 * PRD MS-1 budgets sixty seconds for this, so the shape of the screen is chosen to make
 * that reachable: one text field per team that stays focused so a squad is typed as
 * `Name ⏎ Name ⏎ Name ⏎`, batting order taken from the order they were entered, and the
 * XI defaulted to everyone entered rather than being a separate selection step.
 *
 * MS-2 budgets **twenty** seconds for the second match with the same team, and the saved
 * squads at the top of each side are how: picking one fills the roster with the XI and
 * batting order last used (`savedTeams.ts`), so a rematch is a re-selection rather than a
 * re-entry.
 *
 * A player is a name and nothing else — no phone number, ever (PRD ID-3). That is one of
 * the four things the whole product exists to fix, so there is deliberately nowhere here
 * to put one.
 *
 * ## What #43 added, and why it did not cost the budget
 *
 * P0 had one format and no way to say anything about the match beyond its teams. PRD-P1 §7.2
 * wants six formats, a venue, a date, a ball, editable rules and a privacy switch — which is
 * five more decisions on the screen with the tightest budget in the product. Every one of
 * them is therefore **pre-answered**:
 *
 * - the format row starts on T20 and one tap changes the whole RuleSet,
 * - the ball type follows from the format,
 * - the date defaults to now,
 * - the rules editor is collapsed and nobody has to open it,
 * - the match is public unless it is turned off.
 *
 * A scorer who wants none of it types two squads and starts, exactly as before.
 *
 * ## The format a server does not have
 *
 * A match pins its RuleSet **by id** so a later rule edit cannot rewrite a finished scorecard
 * (PRD §6.5), and only T20 is seeded today — the P1 formats arrive with #40. So a template
 * the server does not carry is shown and not selectable while signed in, and says why. The
 * alternative, quietly creating the match under whichever RuleSet does exist, would put "Box
 * Cricket, 6 overs" on the screen and a 20-over T20 in the ledger.
 *
 * A guest publishes nothing, so on that path the template *is* the format and every card
 * works, custom rules included.
 */
import { useEffect, useMemo, useState } from "react";
import type { Player, PlayerId, RuleSet, Team, UserId, WideRule } from "@sportsos/engine";
import { AddPlayerSheet } from "./AddPlayerSheet.tsx";
import { BottomSheet } from "./BottomSheet.tsx";
import { api, ApiRequestError } from "../api.ts";
import { useOnline } from "./useOnline.ts";
import { localStore } from "../localStore.ts";
import type { BallType, EditableRules, FormatChoice } from "../match/formats.ts";
import {
  BALL_TYPES,
  defaultBallType,
  describeFormat,
  formatChoices,
  isCustomised,
  localRuleSet,
  rulesProblem,
  suggestedMaxPerBowler,
  withoutId,
} from "../match/formats.ts";
import { newPlayerId } from "../match/ids.ts";
import { publishMatch, saveTeamNow } from "../match/publish.ts";
import { useSquadPoll } from "./useSquadPoll.ts";
import { readRememberedXi, rememberXi, teamSetupFromSaved } from "../match/savedTeams.ts";
import { setupClock } from "../match/setupClock.ts";
import type { MatchSetup, SquadPlayer, TeamSetup } from "../match/setup.ts";
import { blankSetup, hasXiRoom, savedDefaults, setupProblem, toggleInXi, toggleRole, xi } from "../match/setup.ts";
import { faceFor, initials, winnerOf, type CoinFace } from "../match/toss.ts";
import { recentVenues, rememberVenue, suggestVenues } from "../match/venues.ts";
import { DEFAULT_WAGON_MODE, setWagonMode, WAGON_MODES, type WagonMode } from "../match/wagonPrefs.ts";
import { fromLocalInput, toLocalInput } from "../match/when.ts";

interface Props {
  /** Null for a guest, who is scoring on this device with no account behind it. */
  readonly scorerId: UserId | null;
  readonly onStarted: (setup: MatchSetup) => void;
  readonly onCancel: () => void;
  readonly onSessionLost: () => void;
  readonly dark: boolean;
  readonly onToggleTheme: () => void;
  readonly initialSetup?: MatchSetup | null;
}

export function Setup({
  scorerId,
  onStarted,
  onCancel,
  onSessionLost,
  dark,
  onToggleTheme,
  initialSetup,
}: Props): React.ReactElement {
  const guest = scorerId === null;
  const online = useOnline();
  const [setup, setSetup] = useState<MatchSetup>(() => initialSetup ?? blankSetup());
  const [entry, setEntry] = useState<readonly [string, string]>(["", ""]);
  const [saved, setSaved] = useState<readonly Team[]>([]);
  /** The server's RuleSets, joined to the engine's templates below. */
  const [serverRuleSets, setServerRuleSets] = useState<readonly RuleSet[] | null>(null);
  const [chosenFormat, setChosenFormat] = useState<string>(initialSetup?.ruleSet.name ?? "T20");
  const [customOpen, setCustomOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // Held here, not in the setup, and saved once the match has its final id: publishing replaces the
  // local match id with the server's, so a choice stored under the draft's id would be orphaned.
  const [wagon, setWagon] = useState<WagonMode>(DEFAULT_WAGON_MODE);
  /** What is being typed, and into which side's field. Drives the name suggestions below. */
  const [typing, setTyping] = useState<{ readonly index: 0 | 1; readonly q: string } | null>(null);
  const [suggested, setSuggested] = useState<
    { readonly index: 0 | 1; readonly players: readonly Player[] } | null
  >(null);
  const [selectedPlayer, setSelectedPlayer] = useState<
    { readonly teamIndex: 0 | 1; readonly playerId: PlayerId } | null
  >(null);
  const [addPlayerForTeam, setAddPlayerForTeam] = useState<0 | 1 | null>(null);
  /** The player just added from the sheet, highlighted and scrolled into view above it. */
  const [justAdded, setJustAdded] = useState<string | null>(null);
  useEffect(() => {
    if (justAdded === null) return;
    // Optional call: not every browser (or jsdom) implements it, and the highlight is enough.
    document.querySelector(`[data-player-id="${justAdded}"]`)?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [justAdded]);
  const [coin, setCoin] = useState<{ readonly face: CoinFace; readonly spinning: boolean } | null>(
    null,
  );
  const store = useMemo(() => localStore(), []);
  const clock = useMemo(() => setupClock(store, () => Date.now()), [store]);
  const venues = useMemo(() => recentVenues(store), [store]);
  /** Now, to the minute, as the datetime input wants it. Read once — a ticking default would
   *  rewrite the field under a scorer who had already edited it. */
  const [when, setWhen] = useState(() => toLocalInput(new Date()));

  // The saved squads and the formats, together: both are needed before a match can be
  // created and neither depends on the other, so they are fetched in parallel rather than
  // spending two round trips of a sixty-second budget in series.
  useEffect(() => {
    clock.started();
    if (guest) {
      // Nothing to ask for, and asking would 401. The engine's templates are the whole of
      // the format list on this path.
      setServerRuleSets([]);
      return;
    }
    void (async () => {
      try {
        const [teams, formats] = await Promise.all([api.listTeams(), api.listRuleSets()]);
        setSaved(teams.teams);
        setServerRuleSets(formats.ruleSets);
        if (formats.ruleSets.length === 0) {
          // Reachable only if the seeded formats are missing from the database, which is a
          // deployment fault rather than anything the scorer did. Said plainly, because the
          // alternative is a "Start scoring" button that is disabled with no explanation
          // and a screen that appears to still be loading.
          setFailure("No match formats are configured. This needs fixing on the server.");
        }
      } catch (error: unknown) {
        if (error instanceof ApiRequestError && error.status === 401) {
          onSessionLost();
          return;
        }
        setFailure("Could not load your teams and formats. Check your connection.");
      }
    })();
  }, [guest, onSessionLost]);

  const choices = useMemo(() => formatChoices(serverRuleSets ?? []), [serverRuleSets]);
  const chosen = useMemo(
    () => choices.find((choice) => choice.key === chosenFormat) ?? choices[0] ?? null,
    [choices, chosenFormat],
  );

  /**
   * The format the screen is showing, as a `RuleSet`.
   *
   * Held on the setup rather than beside it, because it is what the scoring screen reads and
   * what the local ledger is folded against — a second copy would be a second answer to "how
   * many overs is this".
   */
  const rules = setup.ruleSet;

  /** Pick a format: the whole RuleSet changes, and so does the default ball. */
  const pickFormat = (choice: FormatChoice): void => {
    setChosenFormat(choice.key);
    setSetup((current) => ({
      ...current,
      ruleSet: choice.ruleSet ?? localRuleSet(choice.template),
      ballType: defaultBallType(choice.template),
    }));
  };

  // The first format to arrive becomes the selection, so the screen is never showing a
  // placeholder RuleSet by the time anything can be started.
  useEffect(() => {
    if (serverRuleSets === null) return;
    // A match that already has a format (started from a tournament fixture) keeps it.
    if (initialSetup != null) return;
    const preferred =
      choices.find((choice) => choice.key === "T20" && (guest || choice.ruleSet !== null)) ??
      choices.find((choice) => guest || choice.ruleSet !== null);
    if (preferred !== undefined) pickFormat(preferred);
  }, [serverRuleSets]);

  /**
   * Names the server already knows, offered under the field being typed into.
   *
   * The point is not saving keystrokes — it is **not forking a person**. A player typed
   * fresh gets a new row, so "Imran" entered by hand for the third week running is three
   * careers with a third of the runs each. Picking a suggestion carries the server's id and
   * `existing: true`, which is what `publish.ts` reads to mean "do not create this one
   * again", so his figures accumulate against one record.
   *
   * Debounced, because a squad is typed as a burst and a request per keystroke would be
   * twenty-two round trips inside a sixty-second budget. Failures are swallowed: this is a
   * convenience over a field that already works, and an error message about an autocomplete
   * would be noise on the screen with the tightest budget in the product.
   */
  useEffect(() => {
    if (typing === null || guest) return undefined;
    const q = typing.q.trim();
    // Two characters, so the first letter of a name does not fetch half the database.
    if (q.length < 2) {
      setSuggested(null);
      return undefined;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      void api
        .searchPlayers(q, "global")
        .then(({ players }) => {
          if (live) setSuggested({ index: typing.index, players: players.slice(0, 6) });
        })
        .catch(() => {
          if (live) setSuggested(null);
        });
    }, 220);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [typing, guest]);

  const pickSaved = (index: 0 | 1, team: Team): void => {
    patchTeam(index, () => teamSetupFromSaved(team, readRememberedXi(store, team.id), rules.playersPerSide));
  };

  /** Add a player the server already has, keeping his id. Adding one already there does nothing. */
  const addExisting = (index: 0 | 1, player: Player): void => {
    patchTeam(index, (team) => team.players.some((p) => p.id === player.id) ? team : ({
      ...team,
      players: [
        ...team.players,
        {
          id: player.id,
          name: player.displayName,
          battingOrder: team.players.length + 1,
          inXi: hasXiRoom(team, rules.playersPerSide),
          existing: true,
          ...savedDefaults(player),
        },
      ],
    }));
    setEntry((current) => (index === 0 ? ["", current[1]] : [current[0], ""]));
    setTyping(null);
    setSuggested(null);
  };

  /**
   * Flip the coin.
   *
   * `Math.random()` here and a pure `faceFor` in `toss.ts`, so the fairness is testable and
   * only the animation lives in the screen. The result *is* the toss — it writes straight
   * into the same field the manual cards write to, because a coin that produced a decorative
   * result the scorer then had to copy in would be a toy.
   */
  const flip = (): void => {
    const face = faceFor(Math.random());
    setCoin({ face, spinning: true });
    window.setTimeout(() => {
      setCoin({ face, spinning: false });
      setSetup((current) => ({
        ...current,
        tossWinnerTeamId: winnerOf(face, [current.teams[0].id, current.teams[1].id]),
      }));
    }, 900);
  };

  const start = async (): Promise<void> => {
    if (starting) return;
    setStarting(true);
    setFailure(null);

    // Trimmed once, here, so what is remembered as a ground and what is stored on the match
    // are the same string — "Shivaji Park " and "Shivaji Park" are one place.
    const venue = setup.venue?.trim() === "" ? null : setup.venue?.trim() ?? null;
    const staged: MatchSetup = { ...setup, venue, scheduledAt: fromLocalInput(when) };

    // ── The guest path ────────────────────────────────────────────────────────
    // Nothing is published, so the local ids stay local and the format is whatever the
    // screen says it is. The ledger is on this device exactly as it is for anyone else
    // (#13); what is missing is a server that has heard of the match, which is what the
    // banner over the pad offers to fix.
    if (guest) {
      if (venue !== null) rememberVenue(store, venue);
      setWagonMode(store, String(staged.matchId), wagon);
      onStarted({ ...staged, scorerId: null });
      setStarting(false);
      return;
    }

    const pinned = chosen?.ruleSet ?? null;
    if (pinned === null) {
      setFailure("That format is not configured on the server yet. Pick another one.");
      setStarting(false);
      return;
    }

    let published: MatchSetup;
    try {
      const result = (await publishMatch(staged, pinned, api)).setup;
      // The server pins the match to a base format by id — there is no create-ruleset
      // API. The local scoring engine always uses the rules the scorer configured on
      // this screen, with the server's id preserved so the match record is valid.
      published = { ...result, ruleSet: { ...staged.ruleSet, id: result.ruleSet.id } };
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.status === 401) {
        onSessionLost();
        return;
      }
      // Named fields matter here: the server's squad and batting-order rules are strict,
      // and "validation failed" with no field would leave a scorer on a ground with no
      // idea which of twenty-two names is the problem.
      const detail =
        error instanceof ApiRequestError
          ? Object.values(error.body.fields ?? {})[0] ?? error.body.message
          : "Starting a match needs a connection. Your teams are still here — try again when you have signal.";
      setFailure(detail);
      setStarting(false);
      return;
    }

    // Outside the try, and deliberately. The match now exists on the server, so nothing
    // after this point may be reported as a failure to create it — a storage error here
    // would otherwise render "could not reach the server" over a match that was created
    // fine, and the scorer would make a second one.
    //
    // Remembered *after* publishing, so the ids stored are the server's and still mean
    // something next match; local ones would restore an XI of players that do not exist.
    for (const team of published.teams) rememberXi(store, team);
    if (venue !== null) rememberVenue(store, venue);

    // Against MS-1 / MS-2. Warned when over budget so it stands out in a tethered log
    // during the field test, the same way sign-in friction does.
    const timing = clock.finished({
      reusedSquad: setup.teams.every((team) => team.existing === true),
      playersTyped: setup.teams.flatMap((team) => team.players).filter((p) => p.existing !== true)
        .length,
    });
    if (timing !== null) {
      const line =
        `setup (${timing.kind}) took ${timing.seconds}s against a ${timing.budgetSeconds}s ` +
        `budget, ${timing.playersTyped} name(s) typed`;
      if (timing.withinBudget) console.info(`[setup] ${line}`, timing);
      else console.warn(`[setup] OVER BUDGET — ${line}`, timing);
    }

    setWagonMode(store, String(published.matchId), wagon);
    onStarted({ ...published, scorerId });
    setStarting(false);
  };

  const patchTeam = (index: 0 | 1, patch: (team: TeamSetup) => TeamSetup): void => {
    setSetup((current) => {
      const teams: [TeamSetup, TeamSetup] = [current.teams[0], current.teams[1]];
      teams[index] = patch(teams[index]);
      return { ...current, teams };
    });
  };

  const patchRules = (patch: Partial<EditableRules>): void => {
    setSetup((current) => {
      const merged = { ...patch };
      if ("overs" in merged) {
        merged.maxOversPerBowler = suggestedMaxPerBowler(merged.overs ?? null);
      }
      return { ...current, ruleSet: { ...current.ruleSet, ...merged } };
    });
  };

  /**
   * Save a side named here to the server, so it can have an invite QR (see `saveTeamNow`).
   * The toss winner points at the team by id, so it follows the team to its new one.
   */
  const saveTeamForInvite = async (index: 0 | 1): Promise<string> => {
    const before = setup.teams[index];
    const saved = await saveTeamNow(before, api);
    setSetup((current) => {
      const teams: [TeamSetup, TeamSetup] = [current.teams[0], current.teams[1]];
      teams[index] = saved;
      return {
        ...current,
        teams,
        tossWinnerTeamId: current.tossWinnerTeamId === before.id ? saved.id : current.tossWinnerTeamId,
      };
    });
    return saved.id;
  };

  // While the invite sheet is open on a saved team, anyone who joins by QR appears in the list.
  const inviteTeam = addPlayerForTeam === null ? null : setup.teams[addPlayerForTeam];
  useSquadPoll(
    inviteTeam?.existing === true ? inviteTeam.id : null,
    addPlayerForTeam !== null,
    (team) => {
      if (addPlayerForTeam === null) return;
      for (const player of team.squad) addExisting(addPlayerForTeam, player);
    },
  );

  const addPlayer = (index: 0 | 1): void => {
    const name = entry[index].trim();
    if (name === "") return;
    patchTeam(index, (team) => ({
      ...team,
      players: [
        ...team.players,
        // Batting order is the order they were typed. A scorer reads the XI off a team
        // sheet in batting order anyway, so asking him to reorder afterwards would be
        // asking him to do the same work twice.
        { id: newPlayerId(), name, battingOrder: team.players.length + 1, inXi: hasXiRoom(team, rules.playersPerSide) },
      ],
    }));
    setEntry((current) => (index === 0 ? ["", current[1]] : [current[0], ""]));
    setTyping(null);
    setSuggested(null);
  };

  const problem = setupProblem(setup, { requireCaptain: !guest });
  const ruleTrouble = rulesProblem(rules);
  const customised = chosen === null ? false : isCustomised(rules, chosen.template);
  const venueText = setup.venue ?? "";
  const suggestedVenues = suggestVenues(venues, venueText);
  const startable =
    problem === null &&
    ruleTrouble === null &&
    !starting &&
    (guest || (chosen?.ruleSet ?? null) !== null);

  return (
    <div className="setup-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" aria-label="Back" onClick={onCancel}>
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">New match</b>
        </span>
        <button
          className="ds-btn ds-btn--ghost ds-btn--sm"
          onClick={onToggleTheme}
          aria-pressed={dark}
        >
          {dark ? "☾ Dark" : "☀ Light"}
        </button>
      </header>

      {/* Said before twenty-two names are typed, not after they fail to publish. Only a hint —
          the browser's idea of "online" is wrong on a captive portal — so it warns and never
          blocks; the publish itself is what reports a real failure. A guest publishes nothing. */}
      {!guest && !online && (
        <p className="ds-hint ds-hint--error" role="status" style={{ marginBottom: "var(--ds-space-3)" }}>
          You are offline. Starting a match needs a connection once — after that you can score
          with no signal. Connect, then start the match.
        </p>
      )}

      {/* ── Format ──────────────────────────────────────────────────────────── */}
      <article className="ds-card" style={{ marginBottom: "var(--ds-space-4)" }}>
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-3)" }}>Match format</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--ds-space-2)" }}>
          {choices.map((choice) => {
            const usable = guest || choice.ruleSet !== null;
            return (
              <button
                key={choice.key}
                className="ds-option"
                aria-pressed={chosen?.key === choice.key && !customised}
                disabled={!usable}
                onClick={() => {
                  pickFormat(choice);
                  setCustomOpen(false);
                }}
                style={{ minWidth: 0 }}
              >
                <span className="ds-option__label">overs</span>
                <span className="ds-option__value" style={{ fontSize: "var(--ds-text-md)" }}>{choice.template.name}</span>
                {usable ? null : (
                  <span style={{ fontSize: "var(--ds-text-2xs)", color: "var(--ds-danger-500)" }}>
                    not on server
                  </span>
                )}
              </button>
            );
          })}

          <button
            className="ds-option"
            aria-pressed={customised || customOpen}
            onClick={() => setCustomOpen(true)}
            style={{ minWidth: 0 }}
          >
            <span className="ds-option__label">&nbsp;</span>
            <span className="ds-option__value" style={{ fontSize: "var(--ds-text-md)" }}>Custom</span>
          </button>
        </div>
        {serverRuleSets === null ? <p className="ds-hint" style={{ marginTop: "var(--ds-space-2)" }}>Loading formats…</p> : null}
        {chosen !== null && !customOpen && !customised ? (
          <p className="ds-hint" style={{ marginTop: "var(--ds-space-2)" }}>{describeFormat(chosen.template)}</p>
        ) : null}
      </article>

      {/* ── Match details ───────────────────────────────────────────────────── */}
      <article className="ds-card" style={{ marginBottom: "var(--ds-space-4)" }}>
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-3)" }}>Match details</p>

        <div className="ds-field">
          <label className="ds-label" htmlFor="venue">Ground</label>
          <input
            className="ds-input"
            id="venue"
            type="text"
            placeholder="Where is it being played?"
            value={venueText}
            onChange={(event) => {
              const value = event.target.value;
              setSetup((current) => ({ ...current, venue: value }));
            }}
          />
          {/* The grounds this phone has used, most recent first. Local, because the API has
              no venue field on a match — see `venues.ts`. */}
          {suggestedVenues.length === 0 ? null : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-1)" }}>
              {suggestedVenues.map((venue) => (
                <button
                  key={venue}
                  className="ds-chip"
                  onClick={() => setSetup((current) => ({ ...current, venue }))}
                >
                  {venue}
                </button>
              ))}
            </div>
          )}
          <p className="ds-hint">Players within 10 km will see this in "Nearby".</p>
        </div>

        <div className="ds-field" style={{ marginTop: "var(--ds-space-4)" }}>
          <label className="ds-label" htmlFor="when">Date and time</label>
          <input
            className="ds-input"
            id="when"
            type="datetime-local"
            value={when}
            onChange={(event) => setWhen(event.target.value)}
          />
        </div>

        <div style={{ marginTop: "var(--ds-space-4)" }}>
          <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Ball</p>
          <div className="ds-options">
            {BALL_TYPES.map((ball) => (
              <button
                key={ball}
                className="ds-option"
                aria-pressed={(setup.ballType ?? "leather") === ball}
                onClick={() => setSetup((current) => ({ ...current, ballType: ball }))}
              >
                <span className="ds-option__value">{BALL_LABELS[ball]}</span>
              </button>
            ))}
          </div>
        </div>
      </article>

      {/* ── Wagon wheel ─────────────────────────────────────────────────────── */}
      <article className="ds-card" style={{ marginBottom: "var(--ds-space-4)" }}>
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-1)" }}>Wagon wheel</p>
        <p className="ds-hint" style={{ marginBottom: "var(--ds-space-3)" }}>
          {wagon === "off"
            ? "Off: scoring is one tap per ball, as always. You can turn it on from the pad at any point in the match."
            : "After a scoring shot, touch the ground where the ball went. Skip is always one tap, and you can turn it off from the pad."}
        </p>
        <div className="ds-options">
          {WAGON_MODES.map(({ mode, label }) => (
            <button
              key={mode}
              className="ds-option"
              aria-pressed={wagon === mode}
              onClick={() => setWagon(mode)}
            >
              <span className="ds-option__value" style={{ fontSize: "var(--ds-text-md)" }}>{label}</span>
            </button>
          ))}
        </div>
      </article>

      {/* ── Advanced rules ──────────────────────────────────────────────────── */}
      <article className="ds-card" style={{ marginBottom: "var(--ds-space-4)", overflow: "hidden" }}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={customOpen}
          onClick={() => setCustomOpen((open) => !open)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setCustomOpen((open) => !open); }}
          style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-2)", cursor: "pointer" }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: "block", fontSize: "var(--ds-text-md)" }}>Customise rules</b>
            <span className="ds-hint" style={{ display: "block", lineHeight: "var(--ds-leading-normal)", overflowWrap: "break-word" }}>
              {customised ? "edited" : rules.name} — {describeFormat(withoutId(rules))}
            </span>
          </span>
          <span style={{ fontSize: "var(--ds-text-lg)", flex: "none" }}>{customOpen ? "▾" : "▸"}</span>
        </div>

        {customOpen ? (
          <RuleEditor
            rules={rules}
            onChange={patchRules}
            warning={null}
          />
        ) : null}
        {ruleTrouble === null ? null : (
          <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{ruleTrouble}</p>
        )}
      </article>

      {/* ── Privacy ─────────────────────────────────────────────────────────── */}
      <article className="ds-card" style={{ marginBottom: "var(--ds-space-4)" }}>
        <label className="ds-choice">
          <input
            type="checkbox"
            className="ds-switch"
            checked={setup.public !== false}
            onChange={() => setSetup((current) => ({ ...current, public: current.public === false }))}
          />
          <span>
            <b style={{ display: "block" }}>Show on CricAI</b>
            <span className="ds-hint">
              {setup.public !== false
                ? "Anyone with the link can follow it, and it appears in the live list."
                : "Only you and anyone you send the link to."}
            </span>
          </span>
        </label>
      </article>

      {/* ── Teams ──────────────────────────────────────────────────────────── */}
      {([0, 1] as const).map((index) => {
        const team = setup.teams[index];
        const playerCount = team.players.length;
        const ready = playerCount >= 2;
        return (
          <article
            key={team.id}
            className="ds-card"
            style={{ padding: "var(--ds-space-4) var(--ds-space-5)", marginBottom: "var(--ds-space-3)" }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: "var(--ds-space-3)" }}>
              <span className={`ds-avatar ds-avatar--sm ${index === 0 ? "ds-avatar--amber" : "ds-avatar--teal"}`} style={{ marginRight: "var(--ds-space-2)" }}>
                {index + 1}
              </span>
              <span className="ds-label" style={{ flex: 1 }}>Team {index + 1}</span>
              {playerCount > 0 ? (
                <span className={`ds-badge ${ready ? "ds-badge--success" : "ds-badge--warning"}`}>
                  {playerCount} player{playerCount !== 1 ? "s" : ""}{ready ? " · ready" : ""}
                </span>
              ) : null}
            </div>

            {/*
              MS-2's twenty seconds. Picking a saved team fills the roster with its squad
              and the XI last used, so the rest of this section is a glance rather than
              eleven lines of typing.
            */}
            {saved.length === 0 ? null : (
              <div style={{ display: "flex", gap: "var(--ds-space-2)", flexWrap: "wrap", marginBottom: "var(--ds-space-3)" }}>
                {saved.map((candidate) => (
                  <button
                    key={candidate.id}
                    className="ds-chip"
                    aria-pressed={team.id === candidate.id}
                    onClick={() => pickSaved(index, candidate)}
                  >
                    {candidate.name}
                  </button>
                ))}
              </div>
            )}

            <div className="ds-field">
              <input
                className="ds-input"
                type="text"
                placeholder="Team name"
                value={team.name}
                onChange={(event) => patchTeam(index, (t) => ({ ...t, name: event.target.value }))}
              />
            </div>

            {/* Player list. C and WK sit on each XI row, so setting the captain and keeper is
                one tap each rather than a trip into the player sheet per role. */}
            {team.players.length > 0 ? (
              <div style={{ marginTop: "var(--ds-space-3)" }}>
                {team.players.map((player) => {
                  const isKeeper = team.keeperId === player.id;
                  const isCaptain = team.captainId === player.id;
                  return (
                    <div
                      key={player.id}
                      data-player-id={player.id}
                      className={`squad-row${player.inXi ? "" : " squad-row--bench"}${justAdded === player.id ? " squad-row--new" : ""}`}
                    >
                      <button
                        className="squad-row__main"
                        onClick={() => setSelectedPlayer({ teamIndex: index, playerId: player.id })}
                      >
                        <span className="squad-row__no">{player.battingOrder}</span>
                        <span className="squad-row__body">
                          <b className="squad-row__name">{player.name}</b>
                          <span className="squad-row__meta">{player.inXi ? "Playing XI" : "Bench"}</span>
                        </span>
                      </button>
                      {player.inXi ? (
                        <>
                          <button
                            className="squad-row__role"
                            aria-pressed={isCaptain}
                            aria-label={`Captain: ${player.name}`}
                            onClick={() => patchTeam(index, (t) => toggleRole(t, "captain", player.id))}
                          >
                            C
                          </button>
                          <button
                            className="squad-row__role"
                            aria-pressed={isKeeper}
                            aria-label={`Wicket keeper: ${player.name}`}
                            onClick={() => patchTeam(index, (t) => toggleRole(t, "keeper", player.id))}
                          >
                            WK
                          </button>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* The squad has no limit; only the XI is capped (CONTEXT.md, Squad). */}
            {guest ? (
              <div style={{ display: "flex", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-3)" }}>
                <input
                  className="ds-input"
                  type="text"
                  placeholder="Player name"
                  value={entry[index]}
                  style={{ flex: 1 }}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEntry((current) =>
                      index === 0 ? [value, current[1]] : [current[0], value],
                    );
                    setTyping({ index, q: value });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addPlayer(index);
                    }
                  }}
                />
                <button
                  className="ds-btn ds-btn--secondary ds-btn--sm"
                  onClick={() => addPlayer(index)}
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                className="ds-btn ds-btn--primary ds-btn--block"
                style={{ marginTop: "var(--ds-space-3)" }}
                onClick={() => setAddPlayerForTeam(index)}
              >
                <span className="ds-btn__icon">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </span>
                Add player
              </button>
            )}

            {/* Legacy suggested players — kept for saved-team search only */}
            {suggested === null || suggested.index !== index ? null : (
              <SuggestedPlayers
                players={suggested.players.filter(
                  (candidate) => !team.players.some((player) => player.id === candidate.id),
                )}
                // Resolved first: a global search also returns registered accounts, whose
                // id is a user id that no squad, keeper or batting position can reference.
                onPick={(player) => {
                  void api
                    .resolvePlayer(player.id)
                    .then((real) => addExisting(index, real))
                    .catch(() => addExisting(index, player));
                }}
              />
            )}

            {team.players.length === 0 ? (
              <p className="ds-hint" style={{ marginTop: "var(--ds-space-3)" }}>
                Add {rules.playersPerSide} players below
              </p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-3)" }}>
                <span className={`ds-badge ${xi(team).length >= rules.playersPerSide ? "ds-badge--success" : "ds-badge--accent"}`}>
                  {xi(team).length}/{rules.playersPerSide} XI
                </span>
                {team.players.length > xi(team).length && (
                  <span className="ds-badge">{team.players.length - xi(team).length} bench</span>
                )}
                {team.captainId === null && (
                  <span className="ds-badge ds-badge--warning">No captain</span>
                )}
                {team.keeperId === null && (
                  <span className="ds-badge ds-badge--warning">No keeper</span>
                )}
                {team.captainId !== null && team.keeperId !== null && xi(team).length >= rules.playersPerSide && (
                  <span className="ds-badge ds-badge--success">Ready</span>
                )}
              </div>
            )}
          </article>
        );
      })}

      {/* ── Toss ───────────────────────────────────────────────────────────── */}
      <section
        className="ds-on-deep ds-surface-court"
        style={{ borderRadius: "var(--ds-radius-xl)", padding: "var(--ds-space-5)", marginBottom: "var(--ds-space-4)" }}
      >
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-3)" }}>Toss</p>

        {/*
          The coin is optional and skippable: the two cards below it are the whole toss on
          their own, and are what a scorer standing at the middle with a real coin uses. This
          is for the game where nobody has one — which at a maidan is most of them.
        */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--ds-space-3)", marginBottom: "var(--ds-space-4)" }}>
          <button className="ds-btn ds-btn--glass" onClick={flip} disabled={coin?.spinning === true}>
            {coin === null ? "Flip a coin" : "Flip again"}
          </button>
          {coin === null ? (
            <span className="ds-hint" style={{ color: "var(--ds-white-a72)" }}>Or just pick the winner below.</span>
          ) : (
            <>
              <span
                style={{
                  display: "inline-grid",
                  placeItems: "center",
                  width: 44,
                  height: 44,
                  borderRadius: "var(--ds-radius-full)",
                  background: "var(--ds-white-a20)",
                  color: "var(--ds-neutral-0)",
                  fontWeight: "var(--ds-weight-bold)" as any,
                  fontSize: "var(--ds-text-lg)",
                  transition: "transform 0.9s",
                  transform: coin.spinning ? "rotateY(720deg)" : "none",
                }}
                aria-hidden="true"
              >
                {coin.spinning ? "?" : initials(setup.teams[coin.face === "heads" ? 0 : 1].name)}
              </span>
              <span className="ds-hint" style={{ color: "var(--ds-white-a72)" }} role="status">
                {coin.spinning
                  ? "Flipping…"
                  : `${setup.teams[coin.face === "heads" ? 0 : 1].name || "Team " +
                      (coin.face === "heads" ? "A" : "B")} won the toss`}
              </span>
            </>
          )}
        </div>

        {/*
          Two team cards and then two election cards, rather than radio buttons. The same
          two taps either way — what changes is the target: a card is a thumb-sized thing
          with the team's name on it, and a radio is a 20px circle beside one. The text of
          each button is unchanged, so what a screen reader announces is what it always was.
        */}
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Toss won by</p>
        <div className="ds-options" style={{ marginBottom: "var(--ds-space-4)" }}>
          {setup.teams.map((team, i) => (
            <button
              key={team.id}
              className="ds-option"
              aria-pressed={setup.tossWinnerTeamId === team.id}
              onClick={() => setSetup((current) => ({ ...current, tossWinnerTeamId: team.id }))}
            >
              <span className="ds-option__value">{team.name || `Team ${i + 1}`}</span>
            </button>
          ))}
        </div>

        <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Elected to</p>
        <div className="ds-segmented" role="tablist" style={{ width: "100%" }}>
          {(["bat", "bowl"] as const).map((decision) => (
            <button
              key={decision}
              className="ds-segmented__item"
              role="tab"
              aria-selected={setup.tossDecision === decision}
              onClick={() => setSetup((current) => ({ ...current, tossDecision: decision }))}
              style={{ flex: 1 }}
            >
              {decision === "bat" ? "Bat first" : "Bowl first"}
            </button>
          ))}
        </div>
      </section>

      {problem === null ? null : (
        <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{problem}</p>
      )}
      {failure === null ? null : (
        <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{failure}</p>
      )}
      <button
        className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
        style={{ marginTop: "var(--ds-space-4)" }}
        disabled={!startable}
        onClick={() => void start()}
      >
        {starting ? (guest ? "Starting…" : "Creating the match…") : "Start match"}
      </button>

      {selectedPlayer !== null && (() => {
        const team = setup.teams[selectedPlayer.teamIndex];
        const player = team.players.find((p) => p.id === selectedPlayer.playerId);
        if (!player) return null;
        const idx = selectedPlayer.teamIndex;
        return (
          <PlayerActionSheet
            player={player}
            xiFull={xi(team).length >= rules.playersPerSide}
            onToggleXi={() => patchTeam(idx, (t) => toggleInXi(t, player.id))}
            onRemove={() =>
              patchTeam(idx, (t) => ({
                ...t,
                keeperId: t.keeperId === player.id ? null : t.keeperId,
                captainId: t.captainId === player.id ? null : t.captainId,
                players: t.players
                  .filter((p) => p.id !== player.id)
                  .map((p, order) => ({ ...p, battingOrder: order + 1 })),
              }))
            }
            onClose={() => setSelectedPlayer(null)}
          />
        );
      })()}

      {addPlayerForTeam !== null && (
        <AddPlayerSheet
          teamId={setup.teams[addPlayerForTeam].id}
          teamName={setup.teams[addPlayerForTeam].name || `Team ${addPlayerForTeam + 1}`}
          existingPlayerIds={setup.teams[addPlayerForTeam].players.map((p) => p.id)}
          existingNames={setup.teams[addPlayerForTeam].players.map((p) => p.name)}
          // A side named here rather than picked from the saved list has a local id until
          // publish creates it, so there is no server-side squad to add to yet.
          teamIsSaved={setup.teams[addPlayerForTeam].existing === true}
          saveTeam={() => saveTeamForInvite(addPlayerForTeam)}
          describeAdded={() =>
            hasXiRoom(setup.teams[addPlayerForTeam], rules.playersPerSide) ? null : "on the bench"
          }
          onPlayerAdded={(player) => {
            addExisting(addPlayerForTeam, player);
            setJustAdded(player.id);
          }}
          onClose={() => { setAddPlayerForTeam(null); setJustAdded(null); }}
        />
      )}
      {/* Room to scroll the last rows above the add sheet, which leaves the page usable. */}
      {addPlayerForTeam !== null && <div aria-hidden="true" style={{ height: "40dvh" }} />}
    </div>
  );
}

const BALL_LABELS: Readonly<Record<BallType, string>> = {
  leather: "Leather",
  tennis: "Tennis",
  tape: "Tape",
};

/**
 * Every rule a scorer may change, in one place, collapsed by default.
 *
 * The fields are the ones a grassroots format actually varies (PRD-P1 §7.2): the length of
 * the game, the shape of an over, and the handful of laws that are switched off in a car park
 * or switched on in a league. Everything else on a `RuleSet` is either structural
 * (`inningsPerSide`) or a detail nobody in this audience adjusts (`byesBreakMaiden`), and a
 * form with twenty controls is one nobody reads.
 */
function RuleEditor({
  rules,
  onChange,
  warning,
}: {
  readonly rules: RuleSet;
  readonly onChange: (patch: Partial<EditableRules>) => void;
  readonly warning: string | null;
}): React.ReactElement {
  const number = (
    label: string,
    field: "overs" | "ballsPerOver" | "maxOversPerBowler" | "playersPerSide",
    value: number | null,
  ): React.ReactElement => (
    <div className="ds-field">
      <label className="ds-label" htmlFor={field}>
        {label}
      </label>
      <input
        className="ds-input"
        id={field}
        type="number"
        inputMode="numeric"
        min={1}
        value={value === null ? "" : value}
        placeholder="no limit"
        onChange={(event) => {
          const raw = event.target.value;
          // An empty box means "no limit" for the two fields that are nullable, and is held
          // as null rather than as 0 — `overs: 0` would terminate an innings before a ball.
          const parsed = raw === "" ? null : Number(raw);
          if (parsed !== null && Number.isNaN(parsed)) return;
          onChange({ [field]: parsed } as Partial<EditableRules>);
        }}
      />
    </div>
  );

  const toggle = (
    label: string,
    field: "lbwEnabled" | "noBallFreeHit" | "lastManStands" | "oneTipOneHandOut" | "oneBounceCatchOut" | "superOverOnTie",
  ): React.ReactElement => (
    <label className="ds-choice">
      <input
        type="checkbox"
        className="ds-switch"
        checked={rules[field]}
        onChange={() => onChange({ [field]: !rules[field] } as Partial<EditableRules>)}
      />
      {label}
    </label>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--ds-space-4)", marginTop: "var(--ds-space-4)" }}>
      {warning === null ? null : <p className="ds-hint ds-hint--error">{warning}</p>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--ds-space-3)" }}>
        {number("Overs", "overs", rules.overs)}
        {number("Balls per over", "ballsPerOver", rules.ballsPerOver)}
        {number("Max per bowler", "maxOversPerBowler", rules.maxOversPerBowler)}
        {number("Players a side", "playersPerSide", rules.playersPerSide)}
      </div>

      <div>
        <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Wides</p>
        <div className="ds-options">
          {(["standard", "leg_side_only", "none"] as const).map((rule) => (
            <button
              key={rule}
              className="ds-option"
              aria-pressed={rules.wideRule === rule}
              onClick={() => onChange({ wideRule: rule satisfies WideRule })}
            >
              <span className="ds-option__value">{WIDE_LABELS[rule]}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
        {toggle("LBW", "lbwEnabled")}
        {toggle("Free hit after a no-ball", "noBallFreeHit")}
        {toggle("Last man stands", "lastManStands")}
        {toggle("One tip one hand", "oneTipOneHandOut")}
        {toggle("Out off one bounce", "oneBounceCatchOut")}
        {toggle("Super over on a tie", "superOverOnTie")}
      </div>
    </div>
  );
}

const WIDE_LABELS: Readonly<Record<WideRule, string>> = {
  standard: "Standard",
  leg_side_only: "Leg side only",
  none: "No wides",
};

/**
 * Names the server already has, matching what is being typed.
 *
 * Rendered as nothing when the filter leaves none, rather than as an empty strip: a label
 * saying "suggestions" over a blank row reads as a lookup that failed.
 */
function SuggestedPlayers({
  players,
  onPick,
}: {
  readonly players: readonly Player[];
  readonly onPick: (player: Player) => void;
}): React.ReactElement | null {
  if (players.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-2)" }}>
      {players.map((player) => {
        const meta = player.phone ?? player.email ?? "";
        return (
          <button
            key={player.id}
            className="ds-list-item"
            onClick={() => onPick(player)}
            aria-label={`Add ${player.displayName}`}
            style={{ padding: "var(--ds-space-2) var(--ds-space-3)" }}
          >
            <span className="ds-list-item__body">
              <b className="ds-list-item__title">{player.displayName}</b>
              {meta && <span className="ds-list-item__meta">{meta}</span>}
            </span>
            <span className="ds-badge ds-badge--accent" style={{ fontSize: "var(--ds-text-2xs)" }}>Add</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * A player shown as a chip with keeper/XI badges and a remove button.
 * Replaces the old `PlayerRow` with a compact chip layout matching the design system.
 */
function PlayerActionSheet({
  player,
  xiFull,
  onToggleXi,
  onRemove,
  onClose,
}: {
  readonly player: SquadPlayer;
  readonly xiFull: boolean;
  readonly onToggleXi: () => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const canToggleXi = player.inXi || !xiFull;

  // Captain and keeper are set on the row itself (C / WK); this sheet keeps what is rarer.
  return (
    <BottomSheet title={player.name} onClose={onClose}>
      <div className="ds-list">
        <label className="ds-list-item" style={{ cursor: canToggleXi ? "pointer" : "not-allowed", opacity: canToggleXi ? 1 : 0.5 }}>
          <span className="ds-list-item__body">
            <b className="ds-list-item__title">In playing XI</b>
            <span className="ds-list-item__meta">
              {player.inXi ? "Currently selected to play" : xiFull ? "XI is full" : "On the bench"}
            </span>
          </span>
          <input type="checkbox" className="ds-switch" checked={player.inXi}
            disabled={!canToggleXi}
            onChange={() => { onToggleXi(); }} />
        </label>
      </div>

      <button
        className="ds-btn ds-btn--secondary ds-btn--block"
        style={{ marginTop: "var(--ds-space-4)", color: "var(--ds-danger-600)" }}
        onClick={() => { onRemove(); onClose(); }}
      >
        Remove from squad
      </button>
    </BottomSheet>
  );
}
